import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from "react-router"
import { Dropdown, Modal } from 'react-bootstrap'
import Select from 'react-select'
import DatePicker from "react-datepicker"

import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'

import { GET, POST, httpOk } from 'helpers/api'
import {
	findKeyFromArrayByValue, formatDate, formatMoney,
	regexPhoneNumber, returnMinDate, returnSign,
	buildAccountSelectOptions, buildCashAccountSelectOptions, getAccountSelectProps, pickCashAccountId
} from 'helpers/helpers'

function Index() {
	const { t } = useTranslation()
	const reduxAccount = useSelector(state => state.account)
	const date = new Date()

	const [data, setData] = useState([])
	const [poses, setPoses] = useState([])
	const [accounts, setAccounts] = useState([])
	const [counterparties, setCounterparties] = useState([])
	const [paymentPurposeTypes, setPaymentPurposeTypes] = useState([])
	const [modalPayment, setModalPayment] = useState(false)
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterDataExist, setFilterDataExist] = useState(false)
	const [filterData, setFilterData] = useState({ pos_id: reduxAccount.pos_id })
	const [pagination, setPagination] = useState({
		url: '/report-employees',
		response: null
	})
	const [selectedItem, setSelectedItem] = useState({ 'created_at': date })

	async function searchWithFilter(nextFilterData = filterData) {
		var filterUrl = "/report-employees"
		var urlParams = ""
		const filterDataCopy = { ...nextFilterData }
		checkFilter(filterDataCopy)

		if (filterDataCopy.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + filterDataCopy.pos_id
		if (filterDataCopy.search)
			urlParams += returnSign(urlParams) + 'search=' + filterDataCopy.search
		if (filterDataCopy.page)
			urlParams += returnSign(urlParams) + 'page=' + filterDataCopy.page

		if (`/report-employees${urlParams}` !== filterUrl)
			setFilterDataExist(true)

		filterUrl = filterUrl + urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ ...pagination, 'url': filterUrl, 'response': response.data })
		}

		setDropdownIsOpen(false)
	}

	async function createExpense(e) {
		e.preventDefault();

		var response = await POST('/expense-web', selectedItem, { loader: true })

		if (httpOk(response)) {
			toggleModalPayment()
			setSelectedItem({ 'created_at': date })
			searchWithFilter()
		}
	}

	async function handleSelectPPT(option) {
		const defaultCoreId = getDefaultEmployeeAccountId()
		setSelectedItem({
			...selectedItem,
			'payment_purpose_id': option.id,
			'account_chef_id': pickCashAccountId(accounts, option.account_chef_id),
			'account_core_id': option.account_core_id ?? selectedItem.account_core_id ?? defaultCoreId,
			'counterparty': option.counterparty ?? null,
		})

		if (option.counterparty) getCounterparty(option)
		if (!option.counterparty) setCounterparties([])
	}

	async function getCounterparty(option) {
		var response = await POST('/helper/counterparties-type', {
			'payment_purpose_id': option.id
		}, { loader: true })

		if (httpOk(response)) {
			setCounterparties((prev) => {
				const list = [...(response.data || [])]
				const current = prev.find(p => p.id === selectedItem.counterparty_id)
				if (current && !list.find(o => o.id === current.id)) {
					list.unshift(current)
				}
				return list
			})
		}
	}

	function toggleModalPayment(bool = false, item = null) {
		var itemCopy = { ...item }
		// Дату — как из календаря: полный Date уходит в JSON временем по UTC, и после
		// полуночи по Ташкенту (до 05:00) оплата ложилась на вчера, на 5 часов раньше
		itemCopy.created_at = formatDate(new Date(), 'yyyy-MM-dd')

		if (bool) {
			itemCopy.pos_id = item.pos_id || filterData.pos_id || reduxAccount.pos_id
			itemCopy.counterparty_id = item.employee_id
			itemCopy.account_core_id = item.account_core_id || getDefaultEmployeeAccountId()
			itemCopy.account_chef_id = item.account_chef_id || null
			itemCopy.payment_purpose_id = item.payment_purpose_id || null
			itemCopy.amount = item.amount || ''
			itemCopy.note = item.note || ''
			setSelectedItem(itemCopy)
			setCounterparties([{ id: item.employee_id, name: item.name }])
		} else {
			itemCopy.amount = ''
			itemCopy.note = null
			itemCopy.counterparty_id = null
			itemCopy.account_core_id = null
			itemCopy.account_chef_id = null
			itemCopy.payment_purpose_id = null
			setSelectedItem(itemCopy)
			setCounterparties([])
		}

		setModalPayment(bool)
	}

	function resetFilter() {
		const nextFilterData = {
			pos_id: null,
			search: '',
			page: null,
		}
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	function checkFilter(currentFilterData = filterData) {
		if (
			currentFilterData.pos_id === null &&
			currentFilterData.search === ''
		) {
			setFilterDataExist(false)
		}
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	async function paginate(page) {
		const nextFilterData = { ...filterData, 'page': page }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	async function getPoses() {
		const response = await GET('/helper/poses')
		if (httpOk(response)) setPoses(response.data)
	}

	async function getAccounts() {
		const response = await GET('/helper/accounts')
		if (httpOk(response)) {
			setAccounts(response.data)
		}
	}

	async function getPaymentPurposeTypes() {
		const response = await GET('/helper/ppt-expense?counterparty=employee')
		if (httpOk(response)) setPaymentPurposeTypes(response.data)
	}

	function getDefaultEmployeeAccountId(list = accounts) {
		const employeeAccount = list.find((item) => item.counterparty === 'employee')
		return employeeAccount?.id ?? null
	}

	const accountOptions = buildAccountSelectOptions(accounts)
	const accountChefOptions = buildCashAccountSelectOptions(accounts)
	const accountSelectProps = getAccountSelectProps(t)

	useEffect(() => {
		getPoses()
		getAccounts()
		getPaymentPurposeTypes()
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header">
				<h4 className="vertical-center h-100">
					{t('employee_debts')} [{pagination?.response?.total}]
				</h4>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex gap-2">
							<FilterSearch
								filterData={filterData}
								setFilterData={setFilterData}
								onSearchChange={(search) => {
									const nextFilterData = { ...filterData, search, page: null }
									setFilterData(nextFilterData)
									searchWithFilter(nextFilterData)
								}}>
							</FilterSearch>
							<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen}>
								<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
									{t('filter')}
								</Dropdown.Toggle>

								<Dropdown.Menu className="dropdown-filter">
									<div className="row">
										<div className="form-group col-md-6">
											<label>{t('pos')}</label>
											<Select
												options={poses}
												value={poses.find(option => option.id === filterData.pos_id) || ''}
												onChange={(option) => setFilterData({ ...filterData, 'pos_id': option.id })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>
									</div>
									<div className="row mt-3">
										<div className="col-md-12">
											<div className="d-flex justify-content-end gap-2">
												<button className="btn btn-secondary"
													onClick={() => setDropdownIsOpen(false)}>
													{t('cancel')}
												</button>
												<button className="btn btn-primary"
													onClick={() => searchWithFilter({ ...filterData, page: null })}>
													{t('apply')}
												</button>
											</div>
										</div>
									</div>
								</Dropdown.Menu>
							</Dropdown>
						</div>
						<div className="d-flex gap-2">
							<div className="vertical-center">
								<h4><b>{t('amount')}</b>:</h4>
							</div>
							<div className="vertical-center">
								<h5 className="text-end">
									<span className={`${data.total_amount >= 0 ? 'text-success' : 'text-danger'}`}>
										{formatMoney(data.total_amount)}
									</span>
								</h5>
							</div>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{filterData.pos_id &&
									<span className="me-2">
										<b>{t('pos')}: </b>
										{findKeyFromArrayByValue(poses, 'id', filterData.pos_id, 'name')}
									</span>
								}
								{filterData.search &&
									<span className="me-2">
										<b>{t('search')}: </b>
										<span>{filterData.search}</span>
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>
									{t('reset')}
								</button>
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered table-hover mb-0">
							<thead>
								<tr>
									<th style={{ 'width': '33px', 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '20%' }}>{t('employee')}</th>
									<th style={{ 'width': '10%' }}>{t("phone")}</th>
									<th style={{ 'width': '60%' }}></th>
									<th style={{ 'width': '10%' }} className="text-end">{t("debt")}</th>
									<th style={{ 'minWidth': '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item, index) => (
									<tr className="tr-middle" key={index}>
										<td className="text-center">{item.row_number}</td>
										<td className="td-ellipsis">
											<Link className="text-primary cursor table-first-column-clickable"
												to={`/report-employees/debts/${item.employee_id}`}>
												{item.name}
											</Link>
										</td>
										<td className="text-nowrap">{regexPhoneNumber(item.phone)}</td>
										<td></td>
										<td className="text-end text-nowrap">
											<div className={item.balance >= 0 ? 'text-primary' : 'text-danger'}>
												<span>{formatMoney(item.balance)}</span>
											</div>
										</td>
										<td>
											<div className="table-dropdown">
												<Dropdown id={`dropdown-${index}`}>
													<Dropdown.Toggle variant="outline-primary">
														<i className="uil uil-ellipsis-v"></i>
													</Dropdown.Toggle>

													<Dropdown.Menu>
														<div className="table-dropdown-item"
															onClick={() => toggleModalPayment(true, item)}>
															<i className="uil uil-transaction"></i>
															{t('payment')}
														</div>
													</Dropdown.Menu>
												</Dropdown>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination
							pagination={pagination}
							paginate={paginate}>
						</FilterPagination>
					}

				</div>
			</div>

			{/* MODAL PAYMENT */}
			<Modal show={modalPayment} animation={false}
				centered dialogClassName="update-modal-width"
 onHide={() => toggleModalPayment()}>
				<Modal.Header closeButton>
					<Modal.Title>{t('income_expense')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={createExpense} autoComplete="off">
						{reduxAccount.role === 'ROLE_OWNER' &&
							<div className="form-group">
								<label>{t('pos')}</label>
								<Select
									options={poses}
									value={poses.find(option => option.id === selectedItem.pos_id)}
									onChange={(option) => setSelectedItem({ ...selectedItem, 'pos_id': option.id })}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id}
								/>
							</div>
						}
						<div className="form-group">
							<label>{t('date')}</label>
							<div className="calendar-input">
								<DatePicker
									showMonthDropdown
									showYearDropdown
									dropdownMode="select"
									yearDropdownItemNumber={2}
									className="form-control"
									dateFormat="dd.MM.yyyy"
									selected={selectedItem.created_at ? new Date(selectedItem.created_at) : ''}
									onChange={(date) => setSelectedItem({ ...selectedItem, 'created_at': formatDate(date, 'yyyy-MM-dd') })}
									minDate={returnMinDate(new Date(), 20)}
									maxDate={new Date()}
								/>
								<i className="uil uil-calendar-alt"></i>
							</div>
						</div>
						<div className="form-group">
							<label>{t('expense_type')}<span className="required-mark">*</span></label>
							<Select
								options={paymentPurposeTypes}
								value={paymentPurposeTypes.find(option => option.id === selectedItem.payment_purpose_id)}
								onChange={(option) => handleSelectPPT(option)}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
						<div className="form-group">
							<label>{t('counterparty')}<span className="required-mark">*</span></label>
							<Select
								isDisabled
								options={counterparties}
								value={counterparties.find(option => option.id && option.id === selectedItem.counterparty_id) || null}
								onChange={(option) => setSelectedItem({ ...selectedItem, 'counterparty_id': option ? option.id : null })}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
						<div className="form-group">
							<label>{t('operation_account')}<span className="required-mark">*</span></label>
							<Select
								isClearable
								{...accountSelectProps}
								options={accountChefOptions}
								value={accountChefOptions.find(option => option.id === selectedItem.account_chef_id) || null}
								onChange={(option) => setSelectedItem({ ...selectedItem, 'account_chef_id': option ? option.id : null })}
							/>
						</div>
						<div className="form-group d-none">
							<label>{t('report_account')}</label>
							<Select
								{...accountSelectProps}
								options={accountOptions}
								value={accountOptions.find(option => option.id === selectedItem.account_core_id) || null}
								onChange={(option) => setSelectedItem({ ...selectedItem, 'account_core_id': option ? option.id : null })}
							/>
						</div>
						<div className="form-group">
							<label>{t('amount')}<span className="required-mark">*</span></label>
							<input type="text" className="form-control" name="amount"
								value={selectedItem.amount ? formatMoney(selectedItem.amount) : ''}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'amount': e.target.value.replace(/[^0-9.]/g, '') })} />
						</div>
						<div className="form-group">
							<label>{t('note')}</label>
							<input type="text" className="form-control" name="note"
								value={selectedItem.note ?? ''}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'note': e.target.value })} />
						</div>
						<div className="d-flex w-100 mt-3 gap-2">
							<button type="button" className="btn btn-outline-warning w-100"
								onClick={() => toggleModalPayment()} tabIndex="-1">
								{t('cancel')}
							</button>
							<button type="submit" className="btn btn-primary w-100"
								disabled={!selectedItem.amount || !selectedItem.payment_purpose_id || !selectedItem.counterparty_id}>
								{t('save')}
							</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>
			{/* MODAL PAYMENT */}
		</>
	)
}

export default Index
